import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import { SafeMarkdown } from "./SafeMarkdown";
import { toast } from "sonner";
import { Sparkles, Calculator, RefreshCw, Building2, User, PiggyBank, CalendarClock, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

const formatCurrency = (v) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", maximumFractionDigits: 0,
  }).format(Number(v || 0));

/**
 * FinanceAdvisor — deep business-finance analysis with entity toggle,
 * live P&L, tax estimate and AI-driven insights.
 * Renders identically inside Dashboard tab or standalone page.
 */
export function FinanceAdvisor({ compact = false }) {
  const { token } = useAuth();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [entityType, setEntityType] = useState("company");
  const [year, setYear] = useState("all");
  const [extraDeductions, setExtraDeductions] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reserve, setReserve] = useState(null);
  const [reserveFreq, setReserveFreq] = useState("monthly");
  const [tracker, setTracker] = useState(null);
  const [logging, setLogging] = useState(false);

  const yearOptions = useMemo(() => {
    const now = new Date();
    const opts = [{ value: "all", label: "All Time" }];
    // Financial years (FYxx)
    const currentFY = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
    for (let y = currentFY; y >= currentFY - 3; y--) {
      const shortY = String(y).slice(-2);
      opts.push({ value: `FY${shortY}`, label: `FY${shortY} (Jul ${y - 1} – Jun ${y})` });
    }
    // Calendar years
    for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) {
      opts.push({ value: String(y), label: `${y} (Calendar)` });
    }
    return opts;
  }, []);

  const fetchAdvisor = useCallback(async (withAI = true) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        entity_type: entityType,
        year,
        extra_deductions: String(Number(extraDeductions || 0)),
        include_ai: String(withAI),
      });
      const { data: d } = await axios.get(
        `${API}/analytics/finance-advisor?${params}`,
        { headers }
      );
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load finance advisor");
    } finally {
      setLoading(false);
    }
  }, [entityType, year, extraDeductions, headers]);

  useEffect(() => { fetchAdvisor(false); /* fast first-load without AI */ }, [fetchAdvisor]);

  const fetchReserve = useCallback(async () => {
    try {
      const { data: r } = await axios.get(
        `${API}/analytics/cash-reserve?entity_type=${entityType}&frequency=${reserveFreq}`,
        { headers }
      );
      setReserve(r);
    } catch (e) {
      console.warn("Cash reserve fetch failed (non-blocking):", e);
    }
  }, [entityType, reserveFreq, headers]);

  useEffect(() => { fetchReserve(); }, [fetchReserve]);

  const fetchTracker = useCallback(async () => {
    try {
      const { data: t } = await axios.get(`${API}/ato-tracker/entries`, { headers });
      setTracker(t);
    } catch (e) {
      console.warn("ATO tracker fetch failed (non-blocking):", e);
    }
  }, [headers]);

  useEffect(() => { fetchTracker(); }, [fetchTracker]);

  const logSetaside = useCallback(async () => {
    if (!reserve) return;
    setLogging(true);
    try {
      const amount = reserve[reserveFreq]?.setaside || 0;
      const today = new Date().toISOString().slice(0, 10);
      await axios.post(`${API}/ato-tracker/entries`, {
        date: today,
        amount,
        recommended_amount: amount,
        frequency: reserveFreq,
        entity_type: entityType,
        note: `Auto-logged ${reserveFreq} set-aside`,
      }, { headers });
      toast.success(`Logged ${formatCurrency(amount)} to ATO reserve`);
      fetchTracker();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log set-aside");
    } finally {
      setLogging(false);
    }
  }, [reserve, reserveFreq, entityType, headers, fetchTracker]);

  const deleteEntry = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/ato-tracker/entries/${id}`, { headers });
      toast.success("Entry removed");
      fetchTracker();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove entry");
    }
  }, [headers, fetchTracker]);

  return (
    <div className="space-y-6" data-testid="finance-advisor">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Finance Advisor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={entityType === "company" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEntityType("company")}
                  className="flex-1"
                  data-testid="entity-company-btn"
                >
                  <Building2 className="w-4 h-4 mr-1" /> Company
                </Button>
                <Button
                  type="button"
                  variant={entityType === "sole_trader" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEntityType("sole_trader")}
                  className="flex-1"
                  data-testid="entity-sole-trader-btn"
                >
                  <User className="w-4 h-4 mr-1" /> Sole Trader
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fa-year">Period</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger id="fa-year" data-testid="fa-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fa-extra">Extra deductions (this run)</Label>
              <Input
                id="fa-extra"
                type="number"
                min="0"
                placeholder="0"
                value={extraDeductions}
                onChange={(e) => setExtraDeductions(e.target.value)}
                data-testid="fa-extra-input"
              />
            </div>
            <div className="space-y-2 flex flex-col justify-end">
              <Button onClick={() => fetchAdvisor(true)} disabled={loading} data-testid="fa-run-btn">
                {loading
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analysing...</>
                  : <><Calculator className="w-4 h-4 mr-2" /> Run AI Advisor</>
                }
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Pulls invoices (ex-GST), expenses, supplier bills, depreciation, super and any other deductions from Settings.
            Company tax: flat 25% base rate. Sole trader: ATO 2024-25 brackets + Medicare Levy.
          </p>
        </CardContent>
      </Card>

      {/* Cash Reserve Planner — always visible (survives entity toggle) */}
      {reserve && (
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="cash-reserve-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <PiggyBank className="w-5 h-5 text-primary" /> Cash Reserve Planner
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Real-time set-aside so your BAS + income tax bill never surprises you.
                  {" "}Based on {reserve.fy.label} — {reserve.fy.percent_elapsed}% of FY elapsed.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-1 bg-muted rounded-md p-1">
                  <button
                    type="button"
                    onClick={() => setReserveFreq("weekly")}
                    className={`px-3 py-1 text-xs rounded ${reserveFreq === "weekly" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                    data-testid="reserve-freq-weekly"
                  >Weekly</button>
                  <button
                    type="button"
                    onClick={() => setReserveFreq("monthly")}
                    className={`px-3 py-1 text-xs rounded ${reserveFreq === "monthly" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                    data-testid="reserve-freq-monthly"
                  >Monthly</button>
                </div>
                <Button
                  size="sm"
                  onClick={logSetaside}
                  disabled={logging || !reserve || (reserve[reserveFreq]?.setaside || 0) <= 0}
                  className="shrink-0"
                  data-testid="log-setaside-btn"
                  title="Log this period's set-aside to the ATO reserve tracker"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {logging ? "Logging..." : "Add to ATO Tracker"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border p-3 bg-background/60">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Average earnings ex-GST</p>
                <p className="text-xl font-bold" data-testid="reserve-earnings">
                  {formatCurrency(reserve[reserveFreq].avg_earnings_ex_gst)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">per {reserveFreq === "weekly" ? "week" : "month"}</p>
              </div>
              <div className="rounded-lg border border-orange-300 p-3 bg-orange-50/50">
                <p className="text-xs text-orange-800 uppercase tracking-wide">Set aside for BAS + tax</p>
                <p className="text-xl font-bold text-orange-700" data-testid="reserve-setaside">
                  {formatCurrency(reserve[reserveFreq].setaside)}
                </p>
                <p className="text-xs text-orange-700/80 mt-1">
                  ≈ {reserve.setaside_rate_pct}% of every dollar
                </p>
              </div>
              <div className="rounded-lg border border-green-300 p-3 bg-green-50/50">
                <p className="text-xs text-green-800 uppercase tracking-wide">Safe to spend</p>
                <p className="text-xl font-bold text-green-700" data-testid="reserve-safe">
                  {formatCurrency(reserve[reserveFreq].safe_to_spend)}
                </p>
                <p className="text-xs text-green-700/80 mt-1">per {reserveFreq === "weekly" ? "week" : "month"}</p>
              </div>
            </div>
            {!compact && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm pt-2 border-t">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">YTD accrued (owe now)</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Income tax accrued</span><span className="font-medium">{formatCurrency(reserve.ytd.income_tax_accrued)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">GST net</span><span className="font-medium">{formatCurrency(reserve.ytd.gst_net)}</span></div>
                  <div className="flex justify-between font-semibold pt-1"><span>Total accrued to date</span><span className="text-orange-700">{formatCurrency(reserve.ytd.total_accrued)}</span></div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Projected full-year liability</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Income tax</span><span className="font-medium">{formatCurrency(reserve.projected_fy.income_tax)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">GST liability</span><span className="font-medium">{formatCurrency(reserve.projected_fy.gst_liability)}</span></div>
                  <div className="flex justify-between font-semibold pt-1"><span>Total {reserve.fy.label}</span><span className="text-red-700">{formatCurrency(reserve.projected_fy.total_liability)}</span></div>
                </div>
              </div>
            )}
            {reserve.next_bas?.due_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3">
                <CalendarClock className="w-4 h-4" />
                <span>Next BAS due <strong className="text-foreground">{reserve.next_bas.due_date}</strong> — in {reserve.next_bas.days_until} days</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ATO Reserve Tracker — running balance vs recommended */}
      {tracker && tracker.summary.entry_count > 0 && (
        <Card data-testid="ato-tracker-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-primary" /> ATO Reserve Tracker
              <span className="text-xs font-normal text-muted-foreground ml-auto">
                {tracker.summary.entry_count} {tracker.summary.entry_count === 1 ? "entry" : "entries"}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Balance</p>
                <p className="text-lg font-bold" data-testid="tracker-balance">{formatCurrency(tracker.summary.balance)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Recommended</p>
                <p className="text-lg font-bold" data-testid="tracker-recommended">{formatCurrency(tracker.summary.total_recommended)}</p>
              </div>
              <div className={`rounded-lg border p-3 ${tracker.summary.delta >= 0 ? "border-green-300 bg-green-50/50" : "border-red-300 bg-red-50/50"}`}>
                <p className={`text-xs uppercase ${tracker.summary.delta >= 0 ? "text-green-800" : "text-red-800"}`}>Delta</p>
                <p className={`text-lg font-bold flex items-center justify-center gap-1 ${tracker.summary.delta >= 0 ? "text-green-700" : "text-red-700"}`} data-testid="tracker-delta">
                  {tracker.summary.delta >= 0
                    ? <CheckCircle2 className="w-4 h-4" />
                    : <AlertTriangle className="w-4 h-4" />}
                  {formatCurrency(tracker.summary.delta)}
                </p>
              </div>
            </div>
            {!compact && (
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recent entries</p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="tracker-entries-list">
                  {tracker.entries.slice(0, 8).map((e) => {
                    const rec = e.recommended_amount ?? 0;
                    const diff = e.amount - rec;
                    return (
                      <div key={e.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-muted/60">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20 shrink-0">{e.date}</span>
                          <span className="font-medium">{formatCurrency(e.amount)}</span>
                          <span className="text-muted-foreground">
                            / rec {formatCurrency(rec)}
                            {diff !== 0 && (
                              <span className={diff >= 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                                ({diff >= 0 ? "+" : ""}{formatCurrency(diff)})
                              </span>
                            )}
                          </span>
                          <span className="text-muted-foreground/70">·</span>
                          <span className="text-muted-foreground text-[10px] uppercase tracking-wide">{e.frequency}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground">bal {formatCurrency(e.running_balance)}</span>
                          <button
                            type="button"
                            onClick={() => deleteEntry(e.id)}
                            className="text-muted-foreground hover:text-red-600 transition-colors"
                            title="Remove entry"
                            data-testid={`delete-entry-${e.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {tracker.summary.delta < 0 && (
                  <p className="text-xs text-red-700 mt-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    You are under-reserved by <strong>{formatCurrency(Math.abs(tracker.summary.delta))}</strong>. Top up to catch up with the recommended target.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!data && !loading && (
        <p className="text-sm text-muted-foreground">No data yet.</p>
      )}

      {data && (
        <>
          {/* Headline cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Revenue Ex-GST</p>
                <p className="text-2xl font-bold text-primary" data-testid="fa-revenue-ex-gst">
                  {formatCurrency(data.revenue.ex_gst)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Inc GST: {formatCurrency(data.revenue.gross_inc_gst)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Deductions</p>
                <p className="text-2xl font-bold text-orange-600" data-testid="fa-deductions">
                  {formatCurrency(data.deductions.total)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Exp {formatCurrency(data.deductions.expenses)} · Bills {formatCurrency(data.deductions.bills)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Est. Tax</p>
                <p className="text-2xl font-bold text-red-600" data-testid="fa-tax">
                  {formatCurrency(data.tax.total_tax)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Effective rate {data.tax.effective_rate}%
                </p>
              </CardContent>
            </Card>
            <Card className="border-primary/30">
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Net Take-Home</p>
                <p className="text-2xl font-bold text-green-700" data-testid="fa-net">
                  {formatCurrency(data.net_take_home)}
                </p>
                <Badge variant="secondary" className="mt-1 text-xs">
                  {data.entity_type === "company" ? "Company (25%)" : "Sole Trader (ATO)"}
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown */}
          {!compact && (
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Deduction breakdown</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {[
                    ["Expenses", data.deductions.expenses],
                    ["Supplier Bills", data.deductions.bills],
                    ["Depreciation", data.deductions.depreciation],
                    ["Superannuation", data.deductions.superannuation],
                    ["Other (Settings)", data.deductions.other_from_settings],
                    ["Extra (this run)", data.deductions.extra_this_run],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-dashed py-1.5">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-medium">{formatCurrency(v)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 font-semibold">
                    <span>Total</span><span>{formatCurrency(data.deductions.total)}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">GST position</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-dashed py-1.5">
                    <span className="text-muted-foreground">GST collected (from invoices)</span>
                    <span className="font-medium">{formatCurrency(data.gst.collected)}</span>
                  </div>
                  <div className="flex justify-between border-b border-dashed py-1.5">
                    <span className="text-muted-foreground">GST paid on expenses</span>
                    <span className="font-medium">{formatCurrency(data.gst.paid_expenses)}</span>
                  </div>
                  <div className="flex justify-between border-b border-dashed py-1.5">
                    <span className="text-muted-foreground">GST paid on bills</span>
                    <span className="font-medium">{formatCurrency(data.gst.paid_bills)}</span>
                  </div>
                  <div className="flex justify-between pt-2 font-semibold">
                    <span>Net owed to ATO</span>
                    <span className={data.gst.net_owed_to_ato >= 0 ? "text-red-600" : "text-green-600"}>
                      {formatCurrency(data.gst.net_owed_to_ato)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground pt-1">
                    Positive = you owe ATO. Negative = ATO refund due.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* AI insights */}
          {data.ai_insights ? (
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" /> Advisor Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none" data-testid="fa-ai-insights">
                  <SafeMarkdown content={data.ai_insights} />
                </div>
              </CardContent>
            </Card>
          ) : !loading && (
            <Card className="border-dashed">
              <CardContent className="pt-6 flex items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  Click <strong>Run AI Advisor</strong> to generate deep, tactical insights for this period.
                </p>
                <Button size="sm" onClick={() => fetchAdvisor(true)} disabled={loading} data-testid="fa-run-btn-2">
                  <Sparkles className="w-4 h-4 mr-2" /> Run AI
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
